import type { ReactNode } from 'react';
import { useBrandingStore } from '@/stores/branding';

interface AuthShellProps {
  /** Content rendered inside the centered card. */
  children: ReactNode;
  /** Heading rendered above the form (e.g. "Anmelden"). */
  title: string;
  /** Supporting line under the title. */
  description?: ReactNode;
  /** Optional mark rendered above the title (e.g. MFA shield). */
  icon?: ReactNode;
}

/**
 * Minimal centered auth layout: full-bleed `login-bg.png` behind a solid
 * Charcoal card so the form stays readable. No side panels, no SSO chrome.
 */
export function AuthShell({ children, title, description, icon }: AuthShellProps) {
  const appName = useBrandingStore((s) => s.app_name);

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-card-hover px-4 py-12">
      <img
        src="/login-bg.png"
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 h-full w-full select-none object-cover"
      />
      <div className="pointer-events-none absolute inset-0 bg-background/20 dark:bg-background/60" />

      <div className="relative w-full max-w-sm">
        <div className="rounded-2xl border border-border bg-card p-7 shadow-xl shadow-black/10 sm:p-8">
          {/* Brand mark — text wordmark keeps it flat and theme-independent */}
          <div className="mb-7 flex items-center justify-center">
            <span className="text-base font-semibold tracking-tight text-foreground">
              {appName}
            </span>
          </div>

          {icon && <div className="mb-4 flex justify-center">{icon}</div>}
          <h1 className={icon ? 'text-center text-xl font-semibold tracking-tight text-foreground' : 'text-xl font-semibold tracking-tight text-foreground'}>
            {title}
          </h1>
          {description && (
            <p className={icon ? 'mt-1.5 text-center text-sm text-muted' : 'mt-1.5 text-sm text-muted'}>{description}</p>
          )}

          <div className="mt-6">{children}</div>
        </div>
      </div>
    </div>
  );
}
